import React, { useEffect, useState } from 'react'
import { Modal, Button, List, Message, Icon } from 'semantic-ui-react'
import { connect } from 'react-redux'
import {
    get_users_communication,
    update_communication_selection,
} from '../store/actions/communication_user'
import { useTranslation } from 'react-i18next'

export function useIsMobile(breakpoint = 768) {
    const [isMobile, setIsMobile] = useState(window.innerWidth < breakpoint)

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < breakpoint);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [breakpoint]);
    return isMobile;
}

const CommunicationsModal = ({
    open,
    onClose,
    userId,
    role,
    getUsersCommunication,
    updateCommunicationSelection,
    onCheckCommunications,
    reloadKey,
    previewCommunication=[], // Optional prop to directly preview a specific communication by ID
}) => {
    const { t } = useTranslation()
    const [communications, setCommunications] = useState(previewCommunication)
    const [currentIndex, setCurrentIndex] = useState(0);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const isMobile = useIsMobile();

    useEffect(() => {
        if (previewCommunication.length >= 1) {
            setCommunications(previewCommunication?.sort((a, b) => b.id - a.id))
        }
    }, [previewCommunication])


    useEffect(() => {
        const checkAndOpen = async () => {
            const comms = await getUsersCommunication(userId, role)
            const filteredWeb = Array.isArray(comms) ? (
                comms
                .filter(comm => (
                    comm?.communication?.sysCompaniesCommunications?.[0]?.send_web === true
                    || comm?.send_web === true
                ))
                .filter(comm => comm?.selected)
                ?.sort((a, b) => b.id - a.id)
            ) : []
            setCommunications(filteredWeb)
            
            // Cuando llegan nuevos comunicados, o cuando se abre el modal, empezamos desde el inicio (el más reciente)
            setCurrentIndex(0)
            setCurrentImageIndex(0)

            if (filteredWeb.length > 0) {
                onCheckCommunications(true)
            } else {
                onCheckCommunications(false)
            }
        }
        if (userId) {
            checkAndOpen()
        }

    }, [userId, role, reloadKey])

    const currentComm = communications[currentIndex]

    // Reset image index when switching messages
    useEffect(() => {
        setCurrentImageIndex(0);
    }, [currentIndex]);

    // Auto-rotate images
    useEffect(() => {
        if (currentComm?.communication?.images?.length > 1) {
            const interval = setInterval(() => {
                setCurrentImageIndex((prev) =>
                    (prev + 1) % currentComm.communication.images.length
                );
            }, 10000);
            return () => clearInterval(interval);
        }
    }, [currentComm, currentImageIndex]);

    const handleDismiss = async (id) => {
        if(!userId) return onClose() // Si estamos en superadministrador no hacemos nada, solo cerramos el modal

        if(communications.find(comm => comm.id === id)?.selected){
            await updateCommunicationSelection(id, false)
        }
        
        const updated = communications.filter((comm) => comm.id !== id)?.sort((a, b) => b.id - a.id)
        setCommunications(updated)

        if (updated.length === 0) {
            onClose()
        } else {
            setCurrentIndex((prev) =>
                prev >= updated.length ? updated.length - 1 : prev
            )
        }
    }

    const handleDismissAll = async () => {
        try {
            if(!userId) return onClose() // Si estamos en superadministrador no hacemos nada, solo cerramos el modal
            
            await Promise.all(
                communications.map((comm) => {
                    updateCommunicationSelection(comm.id, false);
                })
            )
            setCommunications([]);
            setCurrentIndex(0);
            onClose();
        } catch (error) {
            console.error("Error al descartar todos los avisos", error);
        }
    }

    const handleNext = () => {
        setCurrentIndex((prev) => (prev + 1) % communications.length)
    }

    const handlePrev = () => {
        setCurrentIndex((prev) =>
            prev === 0 ? communications.length - 1 : prev - 1
        )
    }

    if (communications.length === 0) {
        return null
    }



    const formatMessage = (message) => {

        let formatted = message.replace(/\[(.+?)\]\((.+?)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" style="color: #1a73e8; text-decoration: underline; cursor: pointer;">$1</a>');
        formatted = formatted.replace(/\*(.*?)\*/g, '<strong>$1</strong>');

        // Regular expression to match common email endings
        const emailPattern =
            /([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.(com|net|org|gov|edu|mil|info|biz|io|co|me|us))/g

        formatted = formatted.replace(
            emailPattern,
            '<strong>$1</strong>'
        )

        return (
            <div
                style={{
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                }}
                dangerouslySetInnerHTML={{ __html: formatted }}
            />
        )
    }

    return (
        <Modal
            open={open}
            onClose={onClose}
            size='small'
            style={{
                maxWidth: '700px',
                width: '100%',
                maxHeight: '80vh',
                // margin: '20px auto', // Removed fixed margin
                margin: 'auto',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
            }}
        >
            <Modal.Header
                style={{
                    overflowY: 'auto',
                    paddingBottom: '20px', // espacio fijo abajo
                    paddingTop: '20px',
                    textAlign: 'center',
                }}
            >
                <div>
                    <span>
                        {t('Comunicados')} ({currentIndex + 1} {t('de')} {communications.length})
                    </span>
                    <hr color="white" />
                    <div>
                        {formatMessage(
                            currentComm?.communication?.title ||
                            currentComm?.title ||
                            t('Sin título')
                        )}
                    </div>
                </div>

                <Button
                    icon="close"
                    onClick={onClose}
                    style={{
                        position: 'absolute',
                        top: '10px',
                        right: '10px',
                        background: 'transparent',
                        boxShadow: 'none',
                        color: "var(--module-muted, #888)",
                        cursor: 'pointer',
                    }}
                />
            </Modal.Header>


            <Modal.Content
                className="communications-scroll-container" // Scroll styles now apply here
                style={{
                    display: 'flex',
                    alignItems: 'flex-start', // Prevent stretching so sticky works better on children
                    justifyContent: 'space-between',
                    padding: 0,
                    overflowY: 'auto',
                    maxHeight: 'calc(80vh - 140px)', // Scroll constraint moved here
                }}
            >
                {/*Flecha izquierda*/}
                {communications.length > 1 && (
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: isMobile ? '0.2rem' : '0.4rem',
                            position: 'sticky', // Make sticky
                            top: '50%', // Center in viewport
                            zIndex: 1,
                        }}
                    >
                        <Button
                            icon
                            onClick={handlePrev}
                            style={{
                                background: 'transparent',
                                boxShadow: 'none',
                            }}
                        >
                            <Icon name='chevron left' size={isMobile ? 'small' : 'large'} />
                        </Button>
                    </div>
                )}

                {/*Contenido*/}
                <style>
                    {`
                        .communications-scroll-container::-webkit-scrollbar {
                            width: 8px;
                        }
                        .communications-scroll-container::-webkit-scrollbar-track {
                            background: transparent;
                        }
                        .communications-scroll-container::-webkit-scrollbar-thumb {
                            background-color: rgba(0, 0, 0, 0.2);
                            border-radius: 4px;
                        }
                        .communications-scroll-container::-webkit-scrollbar-thumb:hover {
                            background-color: rgba(0, 0, 0, 0.4);
                        }
                    `}
                </style>
                <div
                    style={{
                        flex: 1,
                        padding: isMobile ? '0 0.4rem' : '0.8rem 1.2rem', // Padding moved here to give space to scrollbar
                    }}
                >
                    <div
                        style={{
                            padding: '1rem', // Inner padding for content
                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                            borderRadius: '10px',
                            minHeight: '100%', // Ensure it fills height if content is small
                        }}
                    >
                        {/* <h4 style={{ marginBottom: isMobile ? '0.4em' : '0.8em', textAlign: 'center' }}>
                            {currentComm?.communication?.title || currentComm?.title || t('Sin título')}
                        </h4> */}

                        <div
                            style={{
                                fontSize: '14px',
                            }}
                        >
                            {formatMessage(
                                currentComm?.communication?.message || currentComm?.message || ''
                            )}
                        </div>

                        {/* Image Carousel */}
                        {currentComm?.communication?.images?.length > 0 && (
                            <div style={{ marginTop: '15px', display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
                                <div style={{
                                    width: '100%',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    position: 'relative',
                                    marginBottom: '10px'
                                }}>
                                    {/* Invisible Click Zones for Navigation */}
                                    {currentComm?.communication?.images?.length > 1 && (
                                        <>
                                            <div
                                                onClick={() => setCurrentImageIndex((prev) =>
                                                    prev === 0 ? currentComm.communication.images.length - 1 : prev - 1
                                                )}
                                                style={{
                                                    position: 'absolute',
                                                    top: 0,
                                                    left: 0,
                                                    width: '50%',
                                                    height: '100%',
                                                    zIndex: 10,
                                                    cursor: 'pointer',
                                                    // backgroundColor: 'rgba(255,0,0,0.1)' // Debugging
                                                }}
                                                title={t("Anterior imagen")}
                                            />
                                            <div
                                                onClick={() => setCurrentImageIndex((prev) =>
                                                    (prev + 1) % currentComm.communication.images.length
                                                )}
                                                style={{
                                                    position: 'absolute',
                                                    top: 0,
                                                    right: 0,
                                                    width: '50%',
                                                    height: '100%',
                                                    zIndex: 10,
                                                    cursor: 'pointer',
                                                    // backgroundColor: 'rgba(0,0,255,0.1)' // Debugging
                                                }}
                                                title={t("Siguiente imagen")}
                                            />
                                        </>
                                    )}

                                    <img
                                        src={currentComm.communication?.images[currentImageIndex]?.url}
                                        alt="Communication attachment"
                                        style={{
                                            width: '100%',
                                            height: 'auto',
                                            borderRadius: '8px',
                                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)', // Premium shadow
                                            transition: 'transform 0.3s ease'
                                        }}
                                    />
                                </div>

                                {/* Controls below image (Counter only) */}
                                {currentComm?.communication?.images?.length > 1 && (
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center', // Centered
                                        width: '100%',
                                        marginTop: '8px',
                                        padding: '0 10px'
                                    }}>
                                        {/* Left arrow */}
                                        <i 
                                            class="caret square left outline icon" 
                                            onClick={()=>setCurrentImageIndex((prev) => (prev - 1) % currentComm.communication.images.length )}
                                        />

                                        <div style={{
                                            fontSize: '0.9rem',
                                            color: "var(--module-muted, #777)",
                                            fontWeight: 'bold'
                                        }}>
                                            {currentImageIndex + 1} / {currentComm.communication?.images?.length}
                                        </div>

                                        {/* Right arrow */}
                                        <i 
                                            class="caret square right outline icon"
                                            onClick={()=>setCurrentImageIndex((prev) => (prev + 1) % currentComm.communication.images.length )}
                                        />

                                    </div>
                                )}

                                {/* Image Description */}
                                {currentComm?.communication?.images[currentImageIndex]?.description && (
                                    <div style={{
                                        marginTop: '8px',
                                        textAlign: 'center',
                                        fontStyle: 'italic',
                                        color: "var(--module-text, #555)",
                                        fontSize: '0.95em',
                                        maxWidth: '90%',
                                        lineHeight: '1.4'
                                    }}>
                                        {currentComm.communication.images[currentImageIndex]?.description}
                                    </div>
                                )}
                            </div>
                        )}

                    </div>
                </div>

                {/*Flecha derecha*/}
                {communications.length > 1 && (
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: isMobile ? '0.2rem' : '0.4rem',
                            position: 'sticky', // Make sticky
                            top: '50%', // Center in viewport
                            zIndex: 1,
                        }}
                    >
                        <Button
                            icon
                            onClick={handleNext}
                            style={{
                                background: 'transparent',
                                boxShadow: 'none',
                            }}
                        >
                            <Icon name='chevron right' size={isMobile ? 'small' : 'large'} />
                        </Button>
                    </div>
                )}
            </Modal.Content>
            <Modal.Actions
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: isMobile ? '0.5em 0.75em' : '1em 1.5em',
                }}
            >
                {communications?.filter(i => i.selected).length >= 1 && (
                    <Button
                        onClick={handleDismissAll}
                    >
                        {t('Marcar todos como leídos')}
                    </Button>
                )}
                <Button
                    color='blue'
                    onClick={() => handleDismiss(currentComm?.id)}
                >
                    {t('Aceptar')}
                </Button>
            </Modal.Actions>
        </Modal >
    )
}

const mapDispatchToProps = (dispatch) => ({
    getUsersCommunication: (userId, role) => dispatch(get_users_communication(userId, role)),
    updateCommunicationSelection: (id, selected) =>
        dispatch(update_communication_selection(id, selected)),
})

export default connect(null, mapDispatchToProps)(CommunicationsModal)
